import { ActionIcon, Box, Button, Grid, Group, Text, Tooltip } from '@mantine/core';
import { IconCopy, IconPlus, IconSettings, IconX } from '@tabler/icons-react';
import SearchableCombobox from '../../components/SearchableCombobox';
import AwesomeDebouncePromise from 'awesome-debounce-promise';
import queryString from 'query-string';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router';
import ErrorMessage from '../../components/ErrorMessage';
import MetricsSelector from '../../components/MetricsSelector';
import ConfirmationModal from '../../components/Modals/ConfirmationModal';
import PageSettingsModal from '../../components/Modals/PageSettingsModal';
import NewElementButton from '../../components/NewElementButton';
import PageFilter from '../../components/PageFilter';
import Spinner from '../../components/Spinner';
import copyText from '../../helpers/copyText';
import { daysAgoISODate, dateToEndOfDay, dateToStartOfDay, todayISODate } from '../../helpers/dateUtils';
import { recommenderPageLimitReason } from '../../helpers/recommenderPageLimits';
import useFetch from '../../helpers/useFetch';
import BannerBlocksPage from './bannerBlocks/BannerBlocksPage';
import RecommendersPage from './recommenders/RecommendersPage';

const statsDebounced = AwesomeDebouncePromise((fetch, accountId, domainId, startTs, endTs, type) => {
  return fetch(`/api/admin/accounts/${accountId}/domains/${domainId}/${type.slice(0, -1)}Stats/${encodeURIComponent(startTs)}/${encodeURIComponent(endTs)}`);
}, 300);

const PagesPage = () => {
  const { accountId, domainId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const fetch = useFetch();

  const initialPeriodDays = 7;

  const [pages, setPages] = useState();
  const [stats, setStats] = useState();
  const [error, setError] = useState();
  // Store ISO date strings (YYYY-MM-DD)
  const [startDate, setStartDate] = useState(daysAgoISODate(initialPeriodDays - 1));
  const [endDate, setEndDate] = useState(todayISODate());
  const [confirmationModalPage, setConfirmationModalPage] = useState();
  const [confirmationModalMessage, setConfirmationModalMessage] = useState();
  const [pageSettingsModalOpen, setPageSettingsModalOpen] = useState(false);
  const [recommenderUsageState, setRecommenderUsageState] = useState();
  const usageRequestIds = useRef({});
  const shownUsagePageId = useRef();
  const { type, page, running } = queryString.parse(location.search);

  const recommenderUsagePageId = type === 'recommenders' ? page : undefined;
  // Usage is only valid for the page it was read for, so a response that lands
  // after a page or tab switch can never show on the wrong page.
  const recommenderUsage =
    recommenderUsageState?.pageId === recommenderUsagePageId ? recommenderUsageState?.usage : undefined;
  useEffect(() => {
    shownUsagePageId.current = recommenderUsagePageId;
  }, [recommenderUsagePageId]);
  const refreshRecommenderUsage = useCallback(() => {
    if (!recommenderUsagePageId) {
      setRecommenderUsageState(undefined);
      return;
    }

    const requestId = (usageRequestIds.current[recommenderUsagePageId] =
      (usageRequestIds.current[recommenderUsagePageId] ?? 0) + 1);

    // A refresh handed out for a page that is no longer shown (a toggle that
    // resolves after a page switch) must not touch the page now on screen.
    const store = (usage) => {
      if (
        shownUsagePageId.current === recommenderUsagePageId &&
        requestId === usageRequestIds.current[recommenderUsagePageId]
      ) {
        setRecommenderUsageState({ pageId: recommenderUsagePageId, usage });
      }
    };

    // Informational only — swallowed rather than surfaced as a page error, so a
    // deployment whose API does not serve this route yet still lists and edits
    // the page's product blocks.
    fetch(`/api/admin/accounts/${accountId}/domains/${domainId}/pages/${recommenderUsagePageId}/recommender-limits`)
      .then(store)
      .catch(() => store(undefined));
  }, [fetch, accountId, domainId, recommenderUsagePageId]);

  const goToTab = (key) => {
    const queryObj = queryString.parse(location.search);
    const newQueryObj = Object.assign({}, queryObj, { type: key });
    navigate(`${location.pathname}?${queryString.stringify(newQueryObj)}`);
  };

  const goToPage = useCallback(
    (page, opts = {}) => {
      const queryObj = queryString.parse(location.search);
      const newQueryObj = { ...queryObj, page };
      navigate(`${location.pathname}?${queryString.stringify(newQueryObj)}`, { replace: opts.replace });
    },
    [location.pathname, location.search, navigate]
  );

  useEffect(() => {
    fetch(
      `/api/admin/accounts/${accountId}/domains/${domainId}/pages${
        running === 'true' || running === 'false' ? `?running=${running}` : ''
      }`
    )
      .then(({ pages }) => {
        setPages(pages);
        if (pages.length > 0) {
          if (!page) {
            goToPage(pages[0]?.id, { replace: true });
          }
        } else {
          navigate(`${location.pathname}/new${location.search}`, { replace: true });
        }
      })
      .catch(setError);
  }, [fetch, goToPage, page, accountId, domainId, running, navigate, location.pathname, location.search]);

  useEffect(() => {
    refreshRecommenderUsage();
  }, [refreshRecommenderUsage]);

  useEffect(() => {
    setStats(undefined);
    // Convert ISO date strings to full timestamps for the API
    const startTs = dateToStartOfDay(startDate, 'local');
    const endTs = dateToEndOfDay(endDate, 'local');
    statsDebounced(fetch, accountId, domainId, startTs, endTs, type)
      .then((stats) => {
        setStats(stats);
      })
      .catch((error) => {
        setError(error);
      });
  }, [accountId, domainId, startDate, endDate, fetch, type]);

  if (!type) {
    goToTab('recommenders');
    return;
  }

  const updatePage = (page) => {
    const i = pages.findIndex((p) => p.id.toString() === page.id.toString());
    const updatedPages = structuredClone(pages);
    updatedPages[i] = page;
    setPages(updatedPages);
  };

  const closeConfirmModal = () => {
    setConfirmationModalPage(undefined);
    setConfirmationModalMessage(undefined);
  };

  const deletePage = (pageId, cb) => {
    const currentPage = pages.find((p) => p.id === parseInt(pageId));

    fetch(`/api/admin/accounts/${accountId}/domains/${domainId}/pages/${currentPage.id}`, {
      method: 'DELETE',
    })
      .then(() => {
        const updatedPages = pages.filter((p) => p.id !== currentPage.id);
        setPages(updatedPages);
        goToPage(updatedPages[updatedPages.length - 1]?.id);
        cb();
      })
      .catch(console.error);
  };

  const confirmDeletePage = (pageId) => {
    const currentPage = pages.find((p) => p.id === parseInt(pageId));
    setConfirmationModalPage(currentPage.id);
    setConfirmationModalMessage(`You are about to delete page ${currentPage.name}.`);
  };

  if (error) {
    return <ErrorMessage message={error.message} />;
  }

  if (!pages || !page) {
    return <Spinner />;
  }

  const typeToButtonName = {
    recommenders: 'Product Block',
    bannerBlocks: 'Banner Block',
  };
  const buttonName = `New ${typeToButtonName[type] || ''}`;
  const addBlockedReason = type === 'recommenders' ? recommenderPageLimitReason(recommenderUsage) : null;

  const componentMap = {
    recommenders: <RecommendersPage filter={running} usage={recommenderUsage} refreshUsage={refreshRecommenderUsage} />,
    bannerBlocks: <BannerBlocksPage />,
    postPurchases: <BannerBlocksPage />,
  };

  const pageObj = pages.find((p) => p.id.toString() === page);

  const pageElement = (
    <Grid>
      <Grid.Col span={12}>
        {React.cloneElement(componentMap[type], {
          page: pageObj,
          updatePage,
          stats,
        })}
      </Grid.Col>
    </Grid>
  );

  const pageOptions = pages.map(({ id, name }) => ({ label: name, value: id.toString() }));

  return (
    <div>
      <PageSettingsModal
        isOpen={pageSettingsModalOpen}
        close={() => setPageSettingsModalOpen(false)}
        updated={() => {
          setPageSettingsModalOpen(false);
        }}
        pageId={page}
      />
      <ConfirmationModal
        message={confirmationModalMessage}
        show={!!confirmationModalPage}
        confirm={() => deletePage(confirmationModalPage, () => closeConfirmModal())}
        close={() => closeConfirmModal()}
      />
      <Group mt="md" mb="md" align="flex-end" wrap="wrap" gap="md">
        <MetricsSelector
          startDate={startDate}
          endDate={endDate}
          setStartDate={setStartDate}
          setEndDate={setEndDate}
          rowSize="12"
          scope={type}
        />
        <SearchableCombobox
          options={pageOptions}
          onChange={(value) => goToPage(value)}
          value={page}
          data-tour="pagesSelect"
          style={{ minWidth: 300 }}
        />
        <PageFilter
          placeholder="On / Off"
          queryKey="running"
          overwritePage={false}
          options={[
            { value: 'true', label: 'On' },
            { value: 'false', label: 'Off' },
            { value: 'expired', label: 'Expired' },
            { value: 'scheduled', label: 'Scheduled' },
          ]}
        />
        <Box style={{ flex: 1 }} />
        {addBlockedReason ? (
          // Not NewElementButton's own `disabled`, which also raises the
          // "upgrade your subscription" alert — the wrong explanation here.
          <Tooltip label={addBlockedReason} withArrow>
            <Box display="inline-block">
              <Button size="sm" className="btn-plus-image" data-tour="createRecommenderBtn" disabled>
                <IconPlus width={16} />
                <Text ml="sm">{buttonName}</Text>
              </Button>
            </Box>
          </Tooltip>
        ) : (
          <NewElementButton
            data-tour="createRecommenderBtn"
            onClick={() => navigate(location.pathname + '/' + page + '/' + type + '/new')}
            title={buttonName}
          />
        )}
        <Button onClick={() => navigate(location.pathname + '/new?type=' + type)} title="New Page" variant="default">
          <IconPlus size={16} /> New Page
        </Button>
      </Group>
      <hr />
      <Grid>
        <Grid.Col span={12}>
          <Box display="flex" style={{ justifyContent: 'flex-end', alignItems: 'center' }}>
            <strong style={{ marginRight: 'var(--mantine-spacing-sm)' }}>Token:</strong>
            <span id="p-token">{pages.find((p) => p.id.toString() === page)?.token}</span>
            <ActionIcon variant="subtle" onClick={() => copyText('p-token')}>
              <IconCopy size={16} />
            </ActionIcon>
            <ActionIcon variant="subtle" onClick={() => setPageSettingsModalOpen(true)}>
              <IconSettings size={16} />
            </ActionIcon>
            <ActionIcon variant="subtle" color="red" onClick={() => confirmDeletePage(page)}>
              <IconX size={16} />
            </ActionIcon>
          </Box>
        </Grid.Col>
      </Grid>
      <br />
      {pageElement}
    </div>
  );
};

export default PagesPage;
